import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { MaskedLine, FadeUp } from "@/components/Reveal";
import { useLanguage } from "@/context/LanguageContext";

export default function NewFaces() {
  const { t } = useLanguage();
  useSEO(
    t("nav.newFaces") + " — Diamotims Agency",
    "Emerging talent in development at Diamotims Agency — and how to apply for representation in Montréal."
  );

  const STEPS = [
    { n: "01", title: t("nf.step1Title"), body: t("nf.step1Body") },
    { n: "02", title: t("nf.step2Title"), body: t("nf.step2Body") },
    { n: "03", title: t("nf.step3Title"), body: t("nf.step3Body") },
  ];

  return (
    <main data-testid="new-faces-page" className="pt-28 lg:pt-36">
      <div className="mx-auto max-w-[1800px] px-4 pb-24 sm:px-8 lg:px-16">
        <p className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold sm:text-xs">{t("nf.eyebrow")}</p>
        <h1 className="mt-4 font-display text-5xl font-light uppercase leading-[0.95] tracking-tight text-ivory sm:text-7xl lg:text-8xl">
          <MaskedLine delay={0.1}>{t("nf.title1")}</MaskedLine>
          <MaskedLine delay={0.22}>
            <span className="italic text-gold">{t("nf.title2")}</span>
          </MaskedLine>
        </h1>
        <FadeUp delay={0.3}>
          <p className="mt-8 max-w-2xl font-display text-xl font-light italic leading-relaxed text-white/75 sm:text-2xl">
            {t("nf.intro")}
          </p>
        </FadeUp>

        <FadeUp delay={0.2}>
          <div
            data-testid="new-faces-casting-open"
            className="mt-14 border border-gold/40 bg-[#121212] px-8 py-16 text-center lg:py-24"
          >
            <div className="font-mono text-[10px] uppercase tracking-[0.4em] text-gold">{t("nf.eyebrow")}</div>
            <div className="mt-4 font-display text-4xl font-light uppercase tracking-tight text-ivory sm:text-6xl">
              <span className="italic text-gold">{t("nf.castingOpen")}</span>
            </div>
            <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-white/60">{t("nf.castingBody")}</p>
            <Link
              to="/submissions"
              data-testid="new-faces-casting-apply"
              className="group mt-8 inline-flex items-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold"
            >
              {t("nf.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </div>
        </FadeUp>

        <div className="mt-24 border-t border-white/10 pt-16">
          <FadeUp>
            <h2 className="font-display text-3xl font-light uppercase tracking-tight text-ivory sm:text-5xl">
              {t("nf.howTitle")}
            </h2>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-white/60">{t("nf.howBody")}</p>
          </FadeUp>
          <div className="mt-12 grid grid-cols-1 gap-px border border-white/10 bg-white/10 md:grid-cols-3">
            {STEPS.map((s, i) => (
              <FadeUp key={s.n} delay={i * 0.1} className="bg-[#0A0A0A] p-8 lg:p-10">
                <div className="font-mono text-xs text-gold">{s.n}</div>
                <h3 className="mt-4 font-display text-2xl font-light uppercase tracking-tight text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/60">{s.body}</p>
              </FadeUp>
            ))}
          </div>
          <FadeUp delay={0.2}>
            <Link
              to="/submissions"
              data-testid="new-faces-apply-button"
              className="group mt-10 inline-flex items-center gap-3 bg-ivory px-8 py-4 font-mono text-xs uppercase tracking-[0.3em] text-black transition-colors duration-300 hover:bg-gold"
            >
              {t("nf.cta")}
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1.5" />
            </Link>
          </FadeUp>
        </div>
      </div>
    </main>
  );
}
